import { Cross1Icon, MoonIcon, SunIcon } from '@radix-ui/react-icons';
import { useState } from 'react';

import { Badge } from '../../../../primitives/badge';
import { Button } from '../../../../primitives/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../primitives/popover';
import { Thumbnail } from '../../../../primitives/thumbnail';
import { cn } from '../../../../utils/cn';
import { CONVERT_UNIT, convertBytes } from '../../../../utils/convert-bytes';
import { type FileData } from '../utils/get-files-data';
import { getSeverity, SEVERITY } from '../utils/get-severity';
import { FormName } from './form-name';

interface PreviewPopoverProps {
  disabled?: boolean;
  fileData: FileData;
  sizeAlert?: {
    none: number;
    medium: number;
  };
  onRemove: ({ fileData }: { fileData: FileData }) => void;
  onSubmit: ({ fileData, name }: { fileData: FileData; name: string }) => void;
}

export function PreviewPopover({
  disabled = false,
  fileData,
  sizeAlert,
  onRemove,
  onSubmit,
}: PreviewPopoverProps) {
  const { file, name, src, type } = fileData;
  const [dark, setDark] = useState(false);
  const [open, setOpen] = useState(false);

  const { severity } = getSeverity({ fileSize: file.size, sizeAlert });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className="size-12 rounded-full select-none" disabled={disabled}>
        <div className="relative size-[inherit] rounded-[inherit]">
          <div
            className={cn(
              'absolute top-[-0.5rem] right-[-0.5rem] z-10 m-0 flex size-6 cursor-pointer items-center justify-center rounded-full border bg-background p-0',
              { 'cursor-not-allowed': disabled },
            )}
            onClick={(event) => {
              event.preventDefault();
              onRemove({ fileData });
            }}
          >
            <Cross1Icon className="size-3 max-h-3 max-w-3" />
          </div>
          <Thumbnail
            className={cn(
              'outline outline-2 outline-offset-2 outline-transparent hover:outline-primary',
              {
                'outline-warning': severity === SEVERITY.medium,
                'outline-destructive': severity === SEVERITY.high,
                'cursor-not-allowed outline-muted-foreground/40 hover:outline-muted-foreground/40':
                  disabled,
              },
            )}
            src={src}
            type={type}
            size="sm"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent align="end" className="grid w-64 gap-2" side="right">
        <div
          className={cn('flex justify-center overflow-hidden rounded-lg border', {
            'bg-foreground': dark,
          })}
        >
          <Thumbnail
            className="rounded-none border-none"
            controls
            size="xl"
            src={src}
            type={type}
            variant="contain"
          />
        </div>
        <div className="flex justify-between">
          <Button
            className="rounded-full p-2"
            size="sm"
            variant="outline"
            onClick={() => setDark((prev) => !prev)}
          >
            {dark ? <MoonIcon /> : <SunIcon />}
          </Button>
          <div className="flex gap-2">
            <Badge
              className={cn({
                'bg-warning': severity === SEVERITY.medium,
              })}
              variant={severity === SEVERITY.high ? 'destructive' : 'secondary'}
            >
              {severity === SEVERITY.medium ? <span className="mr-2">Elevado</span> : null}
              {severity === SEVERITY.high ? <span className="mr-2">Crítico</span> : null}
              {convertBytes({ bytes: file.size, to: CONVERT_UNIT.megabyte })}
            </Badge>
            <Badge variant="secondary">{file.type.split('/')[0]}</Badge>
          </div>
        </div>
        <FormName
          name={name}
          onSubmit={({ name }) => {
            onSubmit({ fileData, name });
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
